import { useState, useCallback, useEffect } from 'react';
import { Address } from '../models/AddressModel';
import { normalizeAddressFieldValue } from '../utils/normalizeAddressFieldValue';

export interface AddressFieldValue {
  address_line?: string;
  province?: string;
  ward?: string;
  free_address?: string;
}

export interface UseAddressFieldProps {
  initialAddress?: string | Address | AddressFieldValue | null;
  onAddressChange?: (address: AddressFieldValue) => void;
}

export interface UseAddressFieldReturn {
  addressValue: AddressFieldValue;
  handleAddressChange: (value: AddressFieldValue) => void;
  getAddressFormData: () => {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
  hasAddressData: boolean;
}

export const useAddressField = ({
  initialAddress,
  onAddressChange
}: UseAddressFieldProps): UseAddressFieldReturn => {
  
  // Initialize address value
  const [addressValue, setAddressValue] = useState<AddressFieldValue>(() => {
    if (!initialAddress) {
      return {};
    }

    if (typeof initialAddress === 'string') {
      // If it's a string (UUID), return empty values
      return {};
    }

    return normalizeAddressFieldValue(initialAddress);
  });

  // Re-initialize when initialAddress changes
  useEffect(() => {
    if (!initialAddress) {
      // Don't reset if current value already has data - this prevents clearing when formData becomes UUID
      if (addressValue.address_line || addressValue.province || addressValue.ward || addressValue.free_address) {
        return;
      }
      return;
    }

    if (typeof initialAddress === 'string') {
      // If it's a string (UUID), don't reset if we already have data
      if (addressValue.address_line || addressValue.province || addressValue.ward || addressValue.free_address) {
        return;
      }
      return;
    }

    const newValue = normalizeAddressFieldValue(initialAddress);
    
    // Check if the new value is different from current value to prevent unnecessary updates
    const currentValue = addressValue;
    const isSame = currentValue.address_line === newValue.address_line &&
                  currentValue.province === newValue.province &&
                  currentValue.ward === newValue.ward &&
                  currentValue.free_address === newValue.free_address;
  
    if (isSame) {
      return;
    }
    
    setAddressValue(newValue);
  }, [initialAddress]);

  // Handle address change
  const handleAddressChange = useCallback((value: AddressFieldValue) => {
    setAddressValue(value);
    onAddressChange?.(value);
  }, [onAddressChange]);

  // Get address data for form submission
  const getAddressFormData = useCallback(() => {
    const { address_line, province, ward, free_address } = addressValue;
    
    // Only return fields that have values
    const formData: any = {};
    if (address_line) formData.address_line = address_line;
    if (province) formData.province = province;
    if (ward) formData.ward = ward;
    if (free_address) formData.free_address = free_address;
    
    return formData;
  }, [addressValue]);

  // Check if address has any data
  const hasAddressData = Boolean(
    addressValue.address_line || 
    addressValue.province || 
    addressValue.ward || 
    addressValue.free_address
  );

  return {
    addressValue,
    handleAddressChange,
    getAddressFormData,
    hasAddressData,
  };
};